import type { ApolloClient } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { DISPLAY_NAME_ERROR_NUMBERS, DISPLAY_NAME_MESSAGES } from '@words-happen/display-name';
import {
  MyProfile,
  type MyProfileQuery,
  UpdateDisplayName,
  type UpdateDisplayNameMutation,
  type UpdateDisplayNameMutationVariables,
  WhoAmI,
  type WhoAmIQuery,
} from '../../generated/graphql.api-app';
import { apolloClient } from '../apollo-client';
import { isUnauthenticatedError } from '../settings';

/** The signed-in player's profile, only ever shown to the player themselves. */
export interface Profile {
  /** The confirmed in-game name, or null until they choose one. */
  displayName: string | null;
  /** The name on their account, offered while they have no confirmed name. */
  suggestedDisplayName: string | null;
  email: string | null;
  hasPassword: boolean;
}

/**
 * Why a display name wasn't saved:
 * - `invalid`: the API's validator rejected it, with its message
 * - `unavailable`: moderation rejected it
 * - `too-many-changes`: the player has made 3 changes in 24 hours
 * - `unauthenticated`: the session has expired
 * - `failed`: anything else, such as the network
 */
export type DisplayNameSaveFailure =
  | { kind: 'invalid'; message: string }
  | { kind: 'unavailable' | 'too-many-changes' | 'unauthenticated' | 'failed' };

const fromApiProfile = (profile: MyProfileQuery['myProfile']): Profile => ({
  displayName: profile.displayName ?? null,
  suggestedDisplayName: profile.suggestedDisplayName ?? null,
  email: profile.email ?? null,
  hasPassword: profile.hasPassword,
});

/** The first GraphQL error's code and message, without the "(<code>) " the API puts before the message. */
export const graphQLError = (error: unknown): { code?: string; message: string } | undefined => {
  if (!CombinedGraphQLErrors.is(error)) {
    return undefined;
  }
  const [first] = error.errors;
  const code = first?.extensions?.code;
  return {
    code: typeof code === 'string' ? code : undefined,
    message: (first?.message ?? '').replace(/^\([^)]*\)\s*/, ''),
  };
};

/** Whether `code` is words-happen-service's `<serviceCode>:<number>`, whatever the service code. */
const hasErrorNumber = (code: string | undefined, number: string) => code?.endsWith(`:${number}`) === true;

export const displayNameSaveFailure = (error: unknown): DisplayNameSaveFailure => {
  if (isUnauthenticatedError(error)) {
    return { kind: 'unauthenticated' };
  }

  const graphQL = graphQLError(error);
  if (hasErrorNumber(graphQL?.code, DISPLAY_NAME_ERROR_NUMBERS.invalid)) {
    return { kind: 'invalid', message: graphQL?.message || DISPLAY_NAME_MESSAGES.required };
  }
  if (hasErrorNumber(graphQL?.code, DISPLAY_NAME_ERROR_NUMBERS.unavailable)) {
    return { kind: 'unavailable' };
  }
  if (hasErrorNumber(graphQL?.code, DISPLAY_NAME_ERROR_NUMBERS.tooManyChanges)) {
    return { kind: 'too-many-changes' };
  }
  return { kind: 'failed' };
};

/** Reads the signed-in player's profile and saves their display name on words-happen-gql. Both reject on failure. */
export interface ProfileApi {
  load: () => Promise<Profile>;
  /** Saves the display name and resolves to the profile; rejects with an error `displayNameSaveFailure` explains. */
  saveDisplayName: (displayName: string) => Promise<Profile>;
}

/**
 * The profile API on an Apollo client. A saved name is written into the cached `who`, so the dashboard greeting and
 * every query that uses `who` show it at once, without a reload or another request.
 */
export const createProfileApi = (client: ApolloClient = apolloClient): ProfileApi => ({
  load: async () => {
    const { data } = await client.query<MyProfileQuery>({ query: MyProfile, fetchPolicy: 'network-only' });
    if (!data) {
      throw new Error('myProfile returned no data');
    }
    return fromApiProfile(data.myProfile);
  },
  saveDisplayName: async (displayName) => {
    const { data } = await client.mutate<UpdateDisplayNameMutation, UpdateDisplayNameMutationVariables>({
      mutation: UpdateDisplayName,
      variables: { displayName },
    });
    if (!data) {
      throw new Error('updateDisplayName returned no data');
    }

    const profile = fromApiProfile(data.updateDisplayName);
    client.cache.updateQuery<WhoAmIQuery>({ query: WhoAmI }, (cached) =>
      cached?.who ? { ...cached, who: { ...cached.who, displayName: profile.displayName } } : cached,
    );
    return profile;
  },
});
